import {
  computed,
  getCurrentInstance,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  reactive,
  type ComponentInternalInstance,
} from "vue";

// Components holding the one overlay App.vue renders. Loads that overlap — a dashboard and the
// strategy panel inside it — then show a single overlay until the last of them finishes, instead of
// stacked overlays that each fade out on their own. Holds are per instance so they can't
// double-count: KeepAlive fires onActivated on first mount as well, and a tab KeepAlive hides must
// let go of the overlay without unmounting.
const holders = reactive(new Set<symbol>());

/** Whether any active component holds the full-screen loading overlay. */
export const isLoadingOverlayShown = computed(() => holders.size > 0);

/** Holds the full-screen loading overlay while the calling component is mounted and active. */
export function useLoadingOverlayHold(): void {
  const instance = getCurrentInstance();
  const holder = Symbol();
  const hold = () => holders.add(holder);
  const release = () => holders.delete(holder);
  // A hidden tab still renders, so a store flag can mount a holder inside it. That holder waits for
  // onActivated — which Vue already skips under a deactivated ancestor — rather than taking the
  // overlay over the visible tab.
  onMounted(() => {
    if (!isInDeactivatedTree(instance)) hold();
  });
  onActivated(hold);
  onDeactivated(release);
  onUnmounted(release);
}

function isInDeactivatedTree(
  instance: ComponentInternalInstance | null
): boolean {
  for (let current = instance; current; current = current.parent) {
    if (current.isDeactivated) return true;
  }
  return false;
}
